
import type { SubscriptionDisplayStatus } from '@cvo/domain';
import type { OutstandingBalanceStatus } from '~/components/domain/Payment/OutstandingBalance/OutstandingBalanceModal.model';

const outstandingBalanceStatuses: SubscriptionDisplayStatus[] = ['OVERDUE', 'SUSPENDED', 'CANCELLED'];

export function useOutstandingBalance() {
  const appConfig = useAppConfig();
  const subscriptionStore = useSubscriptionStore();
  const outstandingBalanceStore = useOutstandingBalanceStore();
  const notificationsStore = useNotificationsStore();
  const localePath = useLocalePath();
  const { t } = useI18n();

  function isSupportedStatus(status: SubscriptionDisplayStatus | undefined): status is OutstandingBalanceStatus {
    return !!status && outstandingBalanceStatuses.includes(status);
  }

  const status = computed<OutstandingBalanceStatus | null>(() => {
    if (!appConfig.features.outstandingBalance) return null;
    const displayStatus = subscriptionStore.selectedSubscription?.displayStatus;
    if (!isSupportedStatus(displayStatus)) return null;
    if (displayStatus === 'CANCELLED' && !outstandingBalanceStore.hasOutstandingBalance) return null;
    return displayStatus;
  });

  const subscriptionId = computed<string | null>(() => subscriptionStore.selectedSubscription?.id ?? null);

  const isLoading = computed<boolean>(() => outstandingBalanceStore.isLoading);

  watch(() => subscriptionStore.isSubscriptionPollActive, (active) => {
    if (!active) {
      outstandingBalanceStore.isProcessingRetry = false;
      outstandingBalanceStore.retryProgressStep = 1;
    }
  });

  watch(
    () => subscriptionStore.selectedSubscription,
    (subscription) => {
      const displayStatus = subscription?.displayStatus as SubscriptionDisplayStatus | undefined;
      if (subscription?.id && isSupportedStatus(displayStatus)) {
        outstandingBalanceStore.fetchBalance();
      } else {
        outstandingBalanceStore.reset();
      }
    },
    { immediate: true },
  );

  const isOpen = ref(false);

  async function handleRetryPayment(): Promise<void> {
    if (!outstandingBalanceStore.hasOutstandingBalance) return;

    outstandingBalanceStore.isProcessingRetry = true;

    const previousTotalDue = outstandingBalanceStore.totalDue?.amount;

    await outstandingBalanceStore.retryPayment();

    if (outstandingBalanceStore.retryError) {
      notificationsStore.add({ message: t('ui.subscription.outstandingBalance.retryPaymentError'), status: 'danger' });
      outstandingBalanceStore.isProcessingRetry = false;
      outstandingBalanceStore.retryProgressStep = 1;
      return;
    }

    await outstandingBalanceStore.fetchBalance();

    const balanceChanged = outstandingBalanceStore.totalDue?.amount !== previousTotalDue;

    if (!balanceChanged) {
      notificationsStore.add({ message: t('ui.subscription.outstandingBalance.retryPaymentError'), status: 'danger' });
      outstandingBalanceStore.isProcessingRetry = false;
      outstandingBalanceStore.retryProgressStep = 1;
      return;
    }

    if (outstandingBalanceStore.hasOutstandingBalance) {
      await handleRetryPayment();
      return;
    }

    outstandingBalanceStore.retryProgressStep = 2;

    if (subscriptionId.value) {
      subscriptionStore.pollSubscriptionUntil(() => !isSupportedStatus(subscriptionStore.selectedSubscription?.displayStatus));
    } else {
      outstandingBalanceStore.isProcessingRetry = false;
      outstandingBalanceStore.retryProgressStep = 1;
    }
  }

  function handleUpdatePaymentMethod(): void {
    isOpen.value = false;
    navigateTo(localePath('/billing/change-card'));
  }

  return {
    status,
    subscriptionId,
    isLoading,
    isProcessingRetry: computed<boolean>(() => outstandingBalanceStore.isProcessingRetry),
    retryProgressStep: computed(() => outstandingBalanceStore.retryProgressStep),
    isOpen,
    handleRetryPayment,
    handleUpdatePaymentMethod,
  };
}
